export type LighthouseScores = {
  performance: number;
  accessibility: number;
  bestPractices: number;
  seo: number;
  /** Optional TTFB in milliseconds (AI-OS style ring) */
  ttfbMs?: number;
  /** Optional audited page URL */
  url?: string;
  /** Optional context shown under the chart */
  device?: string;
  date?: string;
  label?: string;
};

export type ImpactMetric = {
  value: string;
  label: string;
};

export type ProjectHighlight = {
  value: string;
  label: string;
};

const LIGHTHOUSE_CATEGORIES: {
  key: keyof Pick<LighthouseScores, "performance" | "accessibility" | "bestPractices" | "seo">;
  label: string;
  short: string;
}[] = [
  { key: "performance", label: "Performance", short: "Perf" },
  { key: "accessibility", label: "Accessibility", short: "A11y" },
  { key: "bestPractices", label: "Best Practices", short: "Best" },
  { key: "seo", label: "SEO", short: "SEO" },
];

export function getLighthouseCategories() {
  return LIGHTHOUSE_CATEGORIES;
}

/** Optional frontmatter: impact: [{ value, label }, ...] */
export function getImpactMetrics(project: {
  id: string;
  impact?: ImpactMetric[];
}): ImpactMetric[] {
  const fromYaml = project.impact;
  if (fromYaml && fromYaml.length > 0) return fromYaml;

  return IMPACT_BY_PROJECT_ID[project.id] ?? [];
}

export function getProjectLighthouse(project: {
  lighthouse?: LighthouseScores;
}): LighthouseScores | undefined {
  return project.lighthouse;
}

/** Optional frontmatter: highlight: { value, label } */
export function getProjectHighlight(project: {
  id: string;
  highlight?: ProjectHighlight;
  description: string;
  excerptMarkdown: string;
  bodyMarkdown: string;
}): ProjectHighlight | undefined {
  if (project.highlight) return project.highlight;

  const preset = HIGHLIGHT_BY_PROJECT_ID[project.id];
  if (preset) return preset;

  const text = [project.description, project.excerptMarkdown, project.bodyMarkdown].join(" ");

  const players = text.match(
    /(?:over\s+)?(\d[\d,.\s]*\+?)\s*(?:active\s+)?(?:players|users|accounts)/i,
  );
  if (players) {
    const value = players[1].replace(/\s/g, "").trim();
    return { value, label: "Active users" };
  }

  return undefined;
}

const IMPACT_BY_PROJECT_ID: Record<string, ImpactMetric[]> = {
  polyphonicabbys: [
    { value: "1 wk", label: "Kickoff to launch" },
    { value: "€760", label: "Estimated project cost" },
    { value: "~2 days", label: "Build time" },
    { value: "0€", label: "Monthly hosting target" },
  ],
  pinkwhale: [
    { value: "4k+", label: "Visits in the first month" },
    { value: "12k+", label: "Visits within months of launch" },
    { value: "€900", label: "Website build estimate" },
    { value: "~2 wks", label: "Core delivery time" },
  ],
  "akademia-fenix": [
    { value: "WP → JS", label: "WordPress to Jamstack migration" },
    { value: "100%", label: "Mobile-first experience" },
    { value: "Netlify", label: "Smart contact via functions" },
    { value: "Lower", label: "Ops cost vs legacy CMS" },
  ],
  wildpoppy: [
    { value: "30+", label: "Plugins consolidated" },
    { value: "~1 wk", label: "Rebuild delivery time" },
    { value: "€1,000", label: "Redevelopment budget" },
    { value: "1 plugin", label: "Custom core plugin" },
  ],
  superunuseful: [
    { value: "1M+", label: "Possible solutions per card" },
    { value: "200", label: "Units per limited edition" },
    { value: "1 rule", label: "Core game constraint" },
    { value: "Hybrid", label: "Physical + digital product" },
  ],
  map4nomads: [
    { value: "~3 mo", label: "Concept to platform" },
    { value: "€15k", label: "Project budget" },
    { value: "1 map", label: "Unified discovery layer" },
    { value: "Mobile", label: "First-class travel UX" },
  ],
  slovakfinance: [
    { value: "~1 mo+", label: "Optimisation & rebuild" },
    { value: "€8k", label: "Project budget" },
    { value: "Faster", label: "Listing & browse performance" },
    { value: "SEO", label: "Indexing & Ads recovery" },
  ],
  "alloy-identity": [
    { value: "500+", label: "Custom hand-drawn icons" },
    { value: "20+", label: "Product module areas" },
    { value: "~3 mo", label: "Identity system delivery" },
    { value: "€10k", label: "Project budget" },
  ],
  "alloy-mobile": [
    { value: "~6 mo", label: "Design & iteration" },
    { value: "€20k", label: "Design budget" },
    { value: "1 app", label: "Unified legacy toolset" },
    { value: "300px", label: "Smallest supported display" },
  ],
  intobridge: [
    { value: "20k+", label: "Active global players" },
    { value: "~3 yr", label: "Principal design lead" },
    { value: "6+", label: "Product surfaces" },
    { value: "Real-time", label: "Ranked + video gameplay" },
  ],
  vydumamky: [
    { value: "30 → 7", label: "Plugins reduced" },
    { value: "~1 wk", label: "Redesign delivery" },
    { value: "€700", label: "Project budget" },
    { value: "Academy", label: "New structured learning area" },
  ],
  alloy: [
    { value: "3 yr", label: "Lead design tenure" },
    { value: "$150k", label: "Design & iteration budget" },
    { value: "300px", label: "Min mobile viewport" },
    { value: "24/7", label: "IoT asset connectivity" },
  ],
  zlobivehovinko: [
    { value: "~3 days", label: "Design to launch" },
    { value: "€600", label: "Project budget" },
    { value: "Static", label: "HTML — no backend" },
    { value: "Live", label: "Official book presentation" },
  ],
  toonect: [
    { value: "SaaS", label: "Cloud call center platform" },
    { value: "Web + Mobile", label: "Unified product surfaces" },
    { value: "B2B", label: "SMB communication focus" },
    { value: "Identity", label: "Brand + product system" },
  ],
  "toonect-mobile": [
    { value: "~3 mo", label: "Design engagement" },
    { value: "€9k", label: "Project budget" },
    { value: "4-in-1", label: "Calls, SMS, chat, email" },
    { value: "iOS + Android", label: "App Store & Play release" },
  ],
  "toonect-identity": [
    { value: "3+", label: "Product surfaces" },
    { value: "1", label: "Unified logo system" },
    { value: "SaaS", label: "Call center platform" },
    { value: "Scale", label: "Module-ready visual language" },
  ],
  "vesmirny-motion": [
    { value: "2012", label: "Landmark SK campaign" },
    { value: "Live", label: "Balloon + stream event" },
    { value: "1st", label: "Domains from near space" },
    { value: "SVG", label: "Parallax motion system" },
  ],
  katastervpapuciach: [
    { value: "~2 wks", label: "Design to delivery" },
    { value: "€800", label: "Project budget" },
    { value: "Mobile", label: "Responsive layout" },
    { value: "Light", label: "Fast, minimal build" },
  ],
  mojapolicia: [
    { value: "Concept", label: "Unpublished exploration" },
    { value: "Civic", label: "Public-service UX study" },
    { value: "Info-first", label: "Support-oriented design" },
    { value: "Neutral", label: "Non-institutional layer" },
  ],
  micina: [
    { value: "WP", label: "Custom WordPress theme" },
    { value: "Mobile", label: "Responsive portfolio" },
    { value: "CMS", label: "Artist self-management" },
    { value: "Brand", label: "Playful identity" },
  ],
  bbq: [
    { value: "Mobile", label: "First-class shop UX" },
    { value: "PrestaShop", label: "Custom theme build" },
    { value: "E-commerce", label: "Full shop redesign" },
    { value: "Brand", label: "Modernised identity" },
  ],
  "elisabeth-mobile": [
    { value: "~1 wk", label: "Design to deployment" },
    { value: "iPad", label: "In-hotel guest app" },
    { value: "Offline", label: "Works without connectivity" },
    { value: "PhoneGap", label: "Hybrid web app" },
  ],
  elisabeth: [
    { value: "WP", label: "WordPress platform" },
    { value: "Mobile", label: "Responsive guest UX" },
    { value: "Booking", label: "Improved guest flows" },
    { value: "CMS", label: "Staff-friendly admin" },
  ],
  aspendos: [
    { value: "Long-term", label: "Platform collaboration" },
    { value: "Flash", label: "ActionScript 3 platform" },
    { value: "Premium", label: "Fashion e-commerce" },
    { value: "Campaigns", label: "Seasonal launch support" },
  ],
  "bio-farma": [
    { value: "1 day", label: "Concept delivery" },
    { value: "€150", label: "Estimated budget" },
    { value: "Proposal", label: "Design pitch stage" },
    { value: "3-in-1", label: "Farm, education, restaurant" },
  ],
  ejedlo: [
    { value: "2015", label: "Pre-delivery-app era" },
    { value: "PoC", label: "Investor-ready concept" },
    { value: "Map", label: "Location-based discovery" },
    { value: "Mobile", label: "Responsive ordering UX" },
  ],
  eoil: [
    { value: "~2 mo", label: "Redesign engagement" },
    { value: "€10k", label: "Estimated budget" },
    { value: "Custom", label: "E-commerce system" },
    { value: "Filters", label: "Advanced product discovery" },
  ],
  mikona: [
    { value: "Seasonal", label: "Adaptable visual system" },
    { value: "Icons", label: "Custom category set" },
    { value: "E-commerce", label: "Retail platform redesign" },
    { value: "Responsive", label: "Desktop + mobile UX" },
  ],
  niejehriech: [
    { value: "Long-term", label: "Active editorial platform" },
    { value: "WP", label: "WordPress implementation" },
    { value: "Editorial", label: "Long-form reading UX" },
    { value: "Luxury", label: "Minimal premium tone" },
  ],
  parkhotel: [
    { value: "~1 mo", label: "Campaign delivery" },
    { value: "Microsite", label: "Conversion landing page" },
    { value: "Digital + Print", label: "Web and billboard" },
    { value: "Reward", label: "Bonus unlock flow" },
  ],
  slavnosti: [
    { value: "2015–16", label: "Festival identity" },
    { value: "Cross-media", label: "Print, web, social" },
    { value: "Identity", label: "Logo + characters" },
    { value: "Incheba", label: "Bratislava expo event" },
  ],
  trila: [
    { value: "~1 mo", label: "Design delivery" },
    { value: "€3k", label: "Estimated budget" },
    { value: "Calculator", label: "Interactive loan tool" },
    { value: "Finance", label: "Conversion-focused UX" },
  ],
  guvernante: [
    { value: "~2 wks", label: "Design to launch" },
    { value: "€1.2k", label: "Project budget" },
    { value: "WP", label: "WordPress website" },
    { value: "Luxury", label: "Premium automotive rental" },
  ],
  "3d": [
    { value: "2014", label: "Platform refinement era" },
    { value: "WP", label: "WordPress-based system" },
    { value: "Iterative", label: "Incremental UX updates" },
    { value: "Responsive", label: "Cross-device improvements" },
  ],
  danyshop: [
    { value: "~1 wk", label: "Redesign delivery" },
    { value: "€1.2k", label: "Project budget" },
    { value: "PrestaShop", label: "Frontend refresh only" },
    { value: "Coffee", label: "Specialty retail focus" },
  ],
  hankadanka: [
    { value: "~2 days", label: "Design to launch" },
    { value: "€600", label: "Project budget" },
    { value: "Static", label: "Lightweight website" },
    { value: "Mobile", label: "Primary audience UX" },
  ],
  hudbazdola: [
    { value: "2014", label: "Music platform launch" },
    { value: "WP", label: "Custom WordPress theme" },
    { value: "Bold", label: "Yellow / black identity" },
    { value: "Identity", label: "Logo + visual system" },
  ],
  kadernictvoparis: [
    { value: "From 0", label: "Full brand from scratch" },
    { value: "WP", label: "WordPress website" },
    { value: "Print", label: "Cards, leaflets, postcards" },
    { value: "Local", label: "Salon promotion focus" },
  ],
  mattcoffin: [
    { value: "<1 day", label: "Rapid delivery" },
    { value: "~€200", label: "Low-budget scope" },
    { value: "Light", label: "Minimal footprint" },
    { value: "PDF", label: "Downloadable resources" },
  ],
  maxinn: [
    { value: "~1 wk", label: "Production timeline" },
    { value: "<€1k", label: "Budget scope" },
    { value: "Flash", label: "ActionScript build" },
    { value: "Motion", label: "Animated storytelling" },
  ],
};

const HIGHLIGHT_BY_PROJECT_ID: Record<string, ProjectHighlight> = {
  intobridge: { value: "20k+", label: "Active players globally" },
  alloy: { value: "98%", label: "Client satisfaction across enterprise rollout" },
  lececil: { value: "SEO 100", label: "Home search readiness" },
};

export function parseGoalCards(
  items: string[],
): { title: string; description: string }[] {
  return items.map((html) => {
    const text = html.replace(/<[^>]*>/g, "").trim();
    const dash = text.indexOf(" - ");
    if (dash > 0) {
      return {
        title: text.slice(0, dash).trim(),
        description: text.slice(dash + 3).trim(),
      };
    }
    const colon = text.indexOf(": ");
    if (colon > 0 && colon < 60) {
      return {
        title: text.slice(0, colon).trim(),
        description: text.slice(colon + 2).trim(),
      };
    }
    return { title: text.slice(0, 48), description: text };
  });
}

/** Parse YAML lighthouse: block into scores (0–100). */
export function parseLighthouseScores(raw: unknown): LighthouseScores | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const row = raw as Record<string, unknown>;

  const readScore = (...keys: string[]): number | undefined => {
    for (const key of keys) {
      const v = row[key];
      if (typeof v === "number" && Number.isFinite(v)) {
        return v <= 1 ? Math.round(v * 100) : Math.round(v);
      }
      if (typeof v === "string" && v.trim() && !Number.isNaN(Number(v))) {
        const n = Number(v);
        return n <= 1 ? Math.round(n * 100) : Math.round(n);
      }
    }
    return undefined;
  };

  const performance = readScore("performance", "perf");
  const accessibility = readScore("accessibility", "a11y");
  const bestPractices = readScore("bestPractices", "best_practices", "best-practices");
  const seo = readScore("seo");

  if (
    performance === undefined ||
    accessibility === undefined ||
    bestPractices === undefined ||
    seo === undefined
  ) {
    return undefined;
  }

  const ttfbRaw = row.ttfbMs ?? row.ttfb_ms ?? row.ttfb;
  let ttfbMs: number | undefined;
  if (typeof ttfbRaw === "number" && Number.isFinite(ttfbRaw)) {
    ttfbMs = Math.round(ttfbRaw);
  } else if (typeof ttfbRaw === "string" && ttfbRaw.trim() && !Number.isNaN(Number(ttfbRaw))) {
    ttfbMs = Math.round(Number(ttfbRaw));
  }

  return {
    performance,
    accessibility,
    bestPractices,
    seo,
    ttfbMs,
    url: typeof row.url === "string" ? row.url : undefined,
    device: typeof row.device === "string" ? row.device : undefined,
    date: typeof row.date === "string" ? row.date : undefined,
    label: typeof row.label === "string" ? row.label : undefined,
  };
}
